import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import WebhookFormDialog from '@/components/integrations/WebhookFormDialog';

interface CrmSetupConfig {
  name: string;
  logoUrl: string;
  description: string;
  whatHobbesSends: string;
  steps: string[];
  note: string;
  docUrl: string;
  docLabel: string;
}

const SAMPLE_PAYLOAD = JSON.stringify(
  {
    event: 'session.qualified',
    timestamp: '2026-01-15T14:30:00Z',
    session: {
      id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
      email: 'jane@acme.com',
      session_url: 'https://app.hihobbes.com/analytics/sessions?sessionId=a1b2c3d4',
      start_time: '2026-01-15T14:25:00Z',
      end_time: '2026-01-15T14:30:00Z',
      duration_seconds: 300,
    },
    sales_summary: {
      primary_intent: 'Exploring enterprise pricing options',
      qualification_status: 'qualified',
      booked_status: 'not_booked',
      session_status: 'completed',
      buying_intent: 'high',
      engagement_level: 'engaged',
      key_topics: ['enterprise plan', 'pricing', 'team onboarding'],
      next_steps: ['Schedule demo call', 'Send pricing proposal'],
      confidence_score: 0.92,
    },
  },
  null,
  2,
);

const CRM_SETUP_DATA: Record<string, CrmSetupConfig> = {
  hubspot: {
    name: 'HubSpot',
    logoUrl: 'https://img.logo.dev/hubspot.com?token=pk_a3-E5RCzS9CzxGENt9PrcQ',
    description: 'Create a HubSpot workflow that receives lead events from Hobbes and creates or updates contacts in your CRM.',
    whatHobbesSends: 'When a visitor qualifies as a lead, books a meeting, or starts an active session, Hobbes sends a JSON payload with their session details and sales summary to your webhook URL.',
    steps: [
      'In HubSpot, go to Automation \u2192 Workflows and click Create workflow.',
      'Select From scratch, then under Custom events & external events, choose Received a webhook from an external app.',
      'Under Advanced options, click When a webhook is received \u2192 + Add a webhook.',
      'Enter a name (e.g., "Hobbes Lead Events") and click Next.',
      'Copy the generated Webhook URL \u2014 you\u2019ll paste this into Hobbes in the next step.',
      'Send a test event from Hobbes (use the "Test" button on the webhooks page) and map the incoming fields to HubSpot properties.',
      'Add workflow actions to create or update contacts based on the incoming data.',
      'Activate the workflow.',
    ],
    note: 'Requires Operations Hub Professional or Enterprise. Webhook triggers only accept application/json content type.',
    docUrl: 'https://knowledge.hubspot.com/workflows/set-when-a-webhook-is-received-workflow-triggers',
    docLabel: 'HubSpot Webhook Triggers Documentation',
  },
  attio: {
    name: 'Attio',
    logoUrl: 'https://img.logo.dev/attio.com?token=pk_a3-E5RCzS9CzxGENt9PrcQ',
    description: 'Create an Attio workflow triggered by an incoming webhook that parses Hobbes lead data and creates a Person record in your CRM.',
    whatHobbesSends: 'When a visitor qualifies as a lead, books a meeting, or starts an active session, Hobbes sends a JSON payload with their session details and sales summary to your webhook URL.',
    steps: [
      'In Attio, go to Automations \u2192 Workflows and click Create workflow.',
      'For the trigger, select Webhook received \u2014 this generates a unique URL endpoint.',
      'Add a Parse JSON block. Define extraction paths for the payload fields (e.g., session.email \u2192 String, sales_summary.qualification_status \u2192 String).',
      'Add a Create or update record block. Select People as the object type and map the parsed JSON outputs to Attio attributes.',
      'Copy the Webhook URL from the trigger step \u2014 you\u2019ll paste this into Hobbes in the next step.',
      'Save and activate the workflow, then use the Hobbes "Test" button to verify delivery.',
    ],
    note: 'All webhook URLs must use HTTPS. Attio signs requests with SHA256 HMAC via the Attio-Signature header for verification.',
    docUrl: 'https://attio.com/help/reference/automations/workflows/workflows-block-library#parse-json',
    docLabel: 'Attio Workflow Blocks Documentation',
  },
};

const CrmSetupPage: React.FC = () => {
  const { crmId } = useParams<{ crmId: string }>();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.id;

  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);

  const config = crmId ? CRM_SETUP_DATA[crmId] : undefined;

  useEffect(() => {
    if (!config) {
      navigate('/integrations', { replace: true });
    }
  }, [config, navigate]);

  if (!config) return null;

  return (
    <>
      <Helmet>
        <title>{config.name} Setup | Integrations | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader
          breadcrumbs={[
            { label: 'Integrations', href: '/integrations' },
            { label: config.name },
          ]}
          titleTooltip="Configure this integration for your workspace."
        />

        <div className="flex-1">
          <div className="px-4 sm:px-6 md:px-8 pb-8 pt-2">
            <div className="space-y-6">
              {/* Intro */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-muted/40 flex items-center justify-center border card-border p-2">
                  <img
                    src={config.logoUrl}
                    alt={`${config.name} logo`}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-heading">{config.name} Integration</h2>
                  <p className="text-sm text-muted-foreground">{config.description}</p>
                </div>
              </div>

              {/* 2-column: instructions left, payload right */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Setup instructions + CTA */}
                <Card className="border card-border shadow-none">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Setup instructions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ol className="space-y-3">
                      {config.steps.map((step, index) => (
                        <li key={index} className="flex gap-3 text-sm">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">
                            {index + 1}
                          </span>
                          <span className="text-foreground pt-0.5">{step}</span>
                        </li>
                      ))}
                    </ol>

                    {config.note && (
                      <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                        {config.note}
                      </div>
                    )}

                    <a
                      href={config.docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      {config.docLabel}
                      <ExternalLink className="h-3 w-3" />
                    </a>

                    <Separator />

                    <div className="flex flex-col items-center text-center gap-3 pt-2">
                      <p className="text-sm text-muted-foreground">
                        Once you have your {config.name} webhook URL, create a webhook in Hobbes to start sending lead events.
                      </p>
                      <Button
                        onClick={() => setWebhookDialogOpen(true)}
                        className="gap-2"
                      >
                        Create Webhook
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* What Hobbes sends */}
                <div className="lg:sticky lg:top-6 lg:self-start">
                  <Card className="border card-border shadow-none">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">What Hobbes sends</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-muted-foreground">{config.whatHobbesSends}</p>
                      <pre className="rounded-md bg-muted p-3 text-xs font-mono overflow-auto">
                        {SAMPLE_PAYLOAD}
                      </pre>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {organizationId && (
        <WebhookFormDialog
          open={webhookDialogOpen}
          onOpenChange={setWebhookDialogOpen}
          organizationId={organizationId}
          defaultName={config.name}
        />
      )}
    </>
  );
};

export default CrmSetupPage;
